import { createActions, handleActions } from "redux-actions";
import fileService from "~libs/fileService";
import { ActionCreatorsOf } from "~libs/actionCreatorsOf";
import { stages } from "./useCurrentStep";
import { esUsoComercial, COMERCIAL, PARTICULAR } from "~constants/usosVehiculo";
import {
  GetMulticotizacionResponse,
  PostMulticotizacionRequest,
  PostSolicitudEmisionRequest,
  PostSolicitudEmisionResponse,
} from "~api/types";
import { DetalleCotizacion } from "~business/cotizaciones/detalleCotizacionTable/types";
import {
  AgenciaSeleccionada,
  CampoPaso,
  ContinuarSolicitudData,
  Dispatch,
  GetState,
  MulticotizadorState,
  OpcionSeleccionada,
  PersonaIdentidad,
  PolizaActual,
  PolizaCotizada,
  Services,
  TelefonoSeleccionado,
} from "./types";

const creators = {
  multicotizador: {
    clear: () => ({}),
    clearAgencia: () => ({}),
    /**
     * El paso arma la agencia con lo que devuelve el selector más `selected`;
     * si el selector entrega null, queda sólo `{ selected: true }`.
     */
    setAgencia: (
      value: Partial<OpcionSeleccionada> & { selected: boolean }
    ) => ({
      value,
    }),
    clearVehiculoMarca: () => ({}),
    setVehiculoMarca: (value: OpcionSeleccionada) => ({ value }),
    clearVehiculoAnio: () => ({}),
    setVehiculoAnio: (value: OpcionSeleccionada) => ({ value }),
    clearVehiculoModelo: () => ({}),
    setVehiculoModelo: (value: OpcionSeleccionada) => ({ value }),
    clearProvincia: () => ({}),
    setProvincia: (value: string | null) => ({ value }),
    clearLocalidad: () => ({}),
    setLocalidad: (value: string | null) => ({ value }),
    clearCodigoPostal: () => ({}),
    setCodigoPostal: (value: string | null) => ({ value }),
    clearVehiculoUso: () => ({}),
    setVehiculoUso: (value: OpcionSeleccionada | null) => ({ value }),
    clearCondicionFiscal: () => ({}),
    setCondicionFiscal: (value: OpcionSeleccionada | null) => ({ value }),
    clearTieneGNC: () => ({}),
    setTieneGNC: (value: CampoPaso<boolean | null>) => ({ value }),
    clearFormaDePago: () => ({}),
    setFormaDePago: (value: OpcionSeleccionada) => ({ value }),
    clearNombre: () => ({}),
    setNombre: (value: string | null) => ({ value }),
    clearApellido: () => ({}),
    setApellido: (value: string | null) => ({ value }),
    clearCuil: () => ({}),
    setCuil: (value: CampoPaso<string | null>) => ({ value }),
    clearDireccion: () => ({}),
    setDireccion: (value: string | null) => ({ value }),
    clearTelefono: () => ({}),
    setTelefono: (value: TelefonoSeleccionado) => ({ value }),
    clearEmail: () => ({}),
    setEmail: (value: CampoPaso<string | null>) => ({ value }),
    clearDominio: () => ({}),
    setDominio: (value: CampoPaso<string | null>) => ({ value }),
    clearMotor: () => ({}),
    setMotor: (value: CampoPaso<string | null>) => ({ value }),
    clearNumeroChasis: () => ({}),
    setNumeroChasis: (value: CampoPaso<string | null>) => ({ value }),
    clearOmitirCargaFotos: () => ({}),
    setOmitirCargaFotos: (value: CampoPaso<string | null>) => ({ value }),
    clearFotoFrenteVehiculo: () => ({}),
    setFotoFrenteVehiculo: (value: CampoPaso<string | null>) => ({ value }),
    clearFotoTraseraVehiculo: () => ({}),
    setFotoTraseraVehiculo: (value: CampoPaso<string | null>) => ({ value }),
    clearFotoLateralIzquierdoVehiculo: () => ({}),
    setFotoLateralIzquierdoVehiculo: (value: CampoPaso<string | null>) => ({
      value,
    }),
    clearFotoLateralDerechoVehiculo: () => ({}),
    setFotoLateralDerechoVehiculo: (value: CampoPaso<string | null>) => ({
      value,
    }),
    clearValidadorDatosPago: () => ({}),
    setValidadorDatosPago: (value: CampoPaso<string | null>) => ({ value }),
    clearMarcaTarjetaCredito: () => ({}),
    setMarcaTarjetaCredito: (value: CampoPaso<string | null>) => ({ value }),
    clearTarjetaDeMercantil: () => ({}),
    setTarjetaDeMercantil: (value: { value: boolean }) => ({ value }),
    clearFechaVencimientoTarjeta: () => ({}),
    setFechaVencimientoTarjeta: (value: CampoPaso<string | null>) => ({
      value,
    }),

    //pasos pendientes a agregar
    clearDni: () => ({}),
    setDni: (value: string | null) => ({ value }),
    clearDniDuplicado: () => ({}),
    setDniDuplicado: (value: PersonaIdentidad[] | null) => ({ value }),
    clearSexo: () => ({}),
    setSexo: (value: CampoPaso<string | null>) => ({ value }),
    /** El paso manda también `esPersonaValida`, que el reducer no lee. */
    setPersona: (value: PersonaIdentidad & { esPersonaValida?: boolean }) => ({
      value,
    }),
    clearEsPersonaValida: () => ({}),
    setEsPersonaValida: (value: CampoPaso<boolean | null>) => ({ value }),
    clearFechaNacimiento: () => ({}),
    setFechaNacimiento: (value: CampoPaso<Date | string | null>) => ({ value }),

    setInitialCurrentPoliza: (value: PolizaActual) => ({ value }),
    clearCurrentPoliza: () => ({}),
    setCurrentPoliza: (value: PolizaActual) => ({ value }),
    clearContratarCurrentPoliza: () => ({}),
    setContratarCurrentPoliza: (value: PolizaActual) => ({ value }),
    clearVigenciaDesde: () => ({}),
    setVigenciaDesde: (value: CampoPaso<Date | null>) => ({ value }),

    setDetalleCotizacion: (value: DetalleCotizacion | null) => ({ value }),
    //
    postRecotizarInited: () => ({}),
    postRecotizarCompleted: (response?: unknown) => ({ response }),
    // Se despacha con el error, pero el payload queda vacío.
    fetchRecotizarFailed: (error?: unknown) => ({}),
    //
    fetchContinuarSolicitudInited: () => ({}),
    fetchContinuarSolicitudCompleted: (response: ContinuarSolicitudData) => ({
      response,
    }),
    // Se despacha con el error, pero el payload queda vacío.
    fetchContinuarSolicitudFailed: (error?: unknown) => ({}),
    //
    fetchIdentidadInited: () => ({}),
    fetchIdentidadCompleted: (response: PersonaIdentidad[]) => ({ response }),
    fetchIdentidadFailed: (error: unknown) => ({ error }),
    //
    postPedidoCotizacionInited: () => ({}),
    /** El payload es el id de la cotización creada. */
    postPedidoCotizacionCompleted: (response: string) => ({ response }),
    postPedidoCotizacionFailed: (error: unknown) => ({ error }),
    //
    postDatosContactoInited: () => ({}),
    postDatosContactoCompleted: (response: PostSolicitudEmisionResponse) => ({
      response,
    }),
    postDatosContactoFailed: (error: unknown) => ({ error }),
    //
    getDetalleCotizacionInited: () => ({}),
    getDetalleCotizacionCompleted: (response: GetMulticotizacionResponse) => {
      let detalleCotizacion: DetalleCotizacion | null = null;

      // Chequeo heredado del JS: la respuesta es un objeto, `.length` da
      // undefined, así que esa parte siempre pasa. Se deja como estaba.
      if (
        typeof response !== "undefined" &&
        response !== null &&
        (response as unknown as unknown[]).length !== 0
      ) {
        detalleCotizacion = response;
      }

      return { detalleCotizacion };
    },
    getDetalleCotizacionFailed: (error: unknown) => ({ error }),
  },
};

const {
  multicotizador: {
    clear,
    clearAgencia,
    setAgencia,
    clearVehiculoMarca,
    setVehiculoMarca,
    clearVehiculoAnio,
    setVehiculoAnio,
    clearVehiculoModelo,
    setVehiculoModelo,
    clearProvincia,
    setProvincia,
    clearLocalidad,
    setLocalidad,
    clearCodigoPostal,
    setCodigoPostal,
    clearVehiculoUso,
    setVehiculoUso,
    clearCondicionFiscal,
    setCondicionFiscal,
    clearTieneGNC,
    setTieneGNC,
    clearFormaDePago,
    setFormaDePago,
    clearDni,
    setDni,
    clearDniDuplicado,
    setDniDuplicado,
    clearSexo,
    setSexo,
    setPersona,
    clearEsPersonaValida,
    setEsPersonaValida,
    clearFechaNacimiento,
    setFechaNacimiento,
    clearNombre,
    setNombre,
    clearApellido,
    setApellido,
    clearCuil,
    setCuil,
    clearDireccion,
    setDireccion,
    clearTelefono,
    setTelefono,
    clearEmail,
    setEmail,
    clearDominio,
    setDominio,
    clearMotor,
    setMotor,
    clearNumeroChasis,
    setNumeroChasis,
    clearFotoFrenteVehiculo,
    setFotoFrenteVehiculo,
    clearFotoTraseraVehiculo,
    setFotoTraseraVehiculo,
    clearOmitirCargaFotos,
    setOmitirCargaFotos,
    clearFotoLateralIzquierdoVehiculo,
    setFotoLateralIzquierdoVehiculo,
    clearFotoLateralDerechoVehiculo,
    setFotoLateralDerechoVehiculo,
    clearValidadorDatosPago,
    setValidadorDatosPago,
    clearMarcaTarjetaCredito,
    setMarcaTarjetaCredito,
    setTarjetaDeMercantil,
    clearTarjetaDeMercantil,
    setFechaVencimientoTarjeta,
    clearFechaVencimientoTarjeta,
    setInitialCurrentPoliza,
    clearCurrentPoliza,
    setCurrentPoliza,
    clearContratarCurrentPoliza,
    setContratarCurrentPoliza,
    clearVigenciaDesde,
    setVigenciaDesde,

    //continuarCotizacion
    postRecotizarInited,
    postRecotizarCompleted,
    fetchRecotizarFailed,
    //continuarCotizacion
    fetchContinuarSolicitudInited,
    fetchContinuarSolicitudCompleted,
    fetchContinuarSolicitudFailed,
    //fetchIdentidad
    fetchIdentidadInited,
    fetchIdentidadCompleted,
    fetchIdentidadFailed,
    //postPedidoCotizacion
    postPedidoCotizacionInited,
    postPedidoCotizacionCompleted,
    postPedidoCotizacionFailed,
    //postDatosContacto
    postDatosContactoInited,
    postDatosContactoCompleted,
    postDatosContactoFailed,
    //getCotizacionDetalle
    getDetalleCotizacionInited,
    getDetalleCotizacionCompleted,
    getDetalleCotizacionFailed,
  },
} = createActions(creators) as unknown as ActionCreatorsOf<typeof creators>;

const initialState: MulticotizadorState = {
  isLoading: false,
  cotizacionId: null,
  detalleCotizacion: null,
  polizasRequestCount: 0,
  multicotizacionInitialStage: stages.initialStage,
  cotizacionARecotizar: null,

  currentPoliza: { value: null, open: false, contratada: false },

  agenciaSelected: { value: null, label: null, selected: false },
  marcaSelected: { value: null, label: null },
  modeloSelected: { value: null, label: null },
  anioSelected: { value: null, label: null },
  provinciaSelected: null,
  localidadSelected: null,
  codigoPostalSelected: null,
  usoSelected: null,
  condicionFiscalSelected: null,
  tieneGNC: { value: null, selected: false },
  formaDePagoSelected: { value: null, label: null },
  dniSelected: null,
  sexoSelected: { value: null, selected: false },
  fechaNacimientoSelected: { value: null, selected: false },
  cuilSelected: { value: null, selected: false },
  dniDuplicado: null,
  nombreSelected: null,
  apellidoSelected: null,
  telefonoSelected: { value: null, isMobile: true },
  esPersonaValida: { value: null, selected: false },
  emailSelected: { value: null, selected: false },
  direccionSelected: null,
  dominioSelected: { value: null, selected: false },
  motorSelected: { value: null, selected: false },
  numeroChasisSelected: { value: null, selected: false },
  validadorDatosPagoSelected: { value: null, selected: false },
  marcaTarjetaCreditoSelected: { value: null, selected: false },
  tarjetaDeMercantil: { value: false },
  fotoFrenteVehiculoSelected: { value: null, selected: false },
  fotoTraseraVehiculoSelected: { value: null, selected: false },
  fotoLateralIzquierdoVehiculoSelected: { value: null, selected: false },
  fotoLateralDerechoVehiculoSelected: { value: null, selected: false },
  fechaVigenciaDesdeSelected: { value: null, selected: false },
  fechaVencimientoTarjetaSelected: { value: null, selected: false },
  sendPostCotizacion: true,
  sendPostSolicitudEmision: true,
};

export const multicotizadorReducer = handleActions<MulticotizadorState, any>(
  {
    [clear.toString()]: (): MulticotizadorState => {
      return {
        ...initialState,
        isForward: true,
      };
    },
    [clearAgencia.toString()]: (
      state: MulticotizadorState,
      action: ReturnType<typeof clearAgencia>
    ): MulticotizadorState => {
      return {
        ...state,
        agenciaSelected: { value: null, label: null, selected: false },
        cotizacionId: null,
        sendPostCotizacion: true,
      };
    },
    [clearVehiculoMarca.toString()]: (
      state: MulticotizadorState,
      action: ReturnType<typeof clearVehiculoMarca>
    ): MulticotizadorState => {
      return {
        ...state,
        modeloSelected: { value: null, label: null },
        anioSelected: { value: null, label: null },
        marcaSelected: { value: null, label: null },
        cotizacionId: null,
        sendPostCotizacion: true,
      };
    },
    [clearVehiculoAnio.toString()]: (
      state: MulticotizadorState,
      action: ReturnType<typeof clearVehiculoAnio>
    ): MulticotizadorState => {
      return {
        ...state,
        modeloSelected: { value: null, label: null },
        anioSelected: { value: null, label: null },
        cotizacionId: null,
        sendPostCotizacion: true,
      };
    },
    [clearVehiculoModelo.toString()]: (
      state: MulticotizadorState,
      action: ReturnType<typeof clearVehiculoModelo>
    ): MulticotizadorState => {
      return {
        ...state,
        modeloSelected: { value: null, label: null },
        cotizacionId: null,
        sendPostCotizacion: true,
      };
    },
    [clearProvincia.toString()]: (
      state: MulticotizadorState,
      action: ReturnType<typeof clearProvincia>
    ): MulticotizadorState => {
      return {
        ...state,
        provinciaSelected: null,
        localidadSelected: null,
        codigoPostalSelected: null,
        cotizacionId: null,
        sendPostCotizacion: true,
      };
    },
    [clearLocalidad.toString()]: (
      state: MulticotizadorState,
      action: ReturnType<typeof clearLocalidad>
    ): MulticotizadorState => {
      return {
        ...state,
        localidadSelected: null,
        codigoPostalSelected: null,
        cotizacionId: null,
        sendPostCotizacion: true,
      };
    },
    [clearCodigoPostal.toString()]: (
      state: MulticotizadorState,
      action: ReturnType<typeof clearCodigoPostal>
    ): MulticotizadorState => {
      return {
        ...state,
        codigoPostalSelected: null,
        cotizacionId: null,
        sendPostCotizacion: true,
      };
    },
    [clearVehiculoUso.toString()]: (
      state: MulticotizadorState,
      action: ReturnType<typeof clearVehiculoUso>
    ): MulticotizadorState => {
      return {
        ...state,
        usoSelected: null,
        cotizacionId: null,
        sendPostCotizacion: true,
      };
    },
    [clearCondicionFiscal.toString()]: (
      state: MulticotizadorState,
      action: ReturnType<typeof clearCondicionFiscal>
    ): MulticotizadorState => {
      return {
        ...state,
        condicionFiscalSelected: null,
        cotizacionId: null,
        sendPostCotizacion: true,
      };
    },
    [clearTieneGNC.toString()]: (
      state: MulticotizadorState,
      action: ReturnType<typeof clearTieneGNC>
    ): MulticotizadorState => {
      return {
        ...state,
        tieneGNC: { value: null, selected: false },
        cotizacionId: null,
        sendPostCotizacion: true,
      };
    },
    [clearFormaDePago.toString()]: (
      state: MulticotizadorState,
      action: ReturnType<typeof clearFormaDePago>
    ): MulticotizadorState => {
      return {
        ...state,
        formaDePagoSelected: { value: null, label: null },
        cotizacionId: null,
        sendPostCotizacion: true,
      };
    },
    [clearDni.toString()]: (
      state: MulticotizadorState,
      action: ReturnType<typeof clearDni>
    ): MulticotizadorState => {
      return {
        ...state,
        dniSelected: null,
        sexoSelected: { value: null, selected: false },
        sendPostSolicitudEmision: true,
      };
    },
    [clearDniDuplicado.toString()]: (
      state: MulticotizadorState,
      action: ReturnType<typeof clearDniDuplicado>
    ): MulticotizadorState => {
      return {
        ...state,
        dniDuplicado: null,
      };
    },
    [clearEsPersonaValida.toString()]: (
      state: MulticotizadorState,
      action: ReturnType<typeof clearEsPersonaValida>
    ): MulticotizadorState => {
      return {
        ...state,
        esPersonaValida: { value: null, selected: false },
        sendPostSolicitudEmision: true,
      };
    },
    [clearSexo.toString()]: (
      state: MulticotizadorState,
      action: ReturnType<typeof clearSexo>
    ): MulticotizadorState => {
      return {
        ...state,
        sexoSelected: { value: null, selected: false },
        esPersonaValida: { value: null, selected: false },
        sendPostSolicitudEmision: true,
      };
    },
    [clearFechaNacimiento.toString()]: (
      state: MulticotizadorState,
      action: ReturnType<typeof clearFechaNacimiento>
    ): MulticotizadorState => {
      return {
        ...state,
        fechaNacimientoSelected: { value: null, selected: false },
        sendPostSolicitudEmision: true,
      };
    },
    [clearNombre.toString()]: (
      state: MulticotizadorState,
      action: ReturnType<typeof clearNombre>
    ): MulticotizadorState => {
      return {
        ...state,
        nombreSelected: null,
        sendPostSolicitudEmision: true,
      };
    },
    [clearApellido.toString()]: (
      state: MulticotizadorState,
      action: ReturnType<typeof clearApellido>
    ): MulticotizadorState => {
      return {
        ...state,
        apellidoSelected: null,
        sendPostSolicitudEmision: true,
      };
    },
    [clearCuil.toString()]: (
      state: MulticotizadorState,
      action: ReturnType<typeof clearCuil>
    ): MulticotizadorState => {
      return {
        ...state,
        cuilSelected: { value: null, selected: false },
        sendPostSolicitudEmision: true,
      };
    },
    [clearDireccion.toString()]: (
      state: MulticotizadorState,
      action: ReturnType<typeof clearDireccion>
    ): MulticotizadorState => {
      return {
        ...state,
        direccionSelected: null,
        sendPostSolicitudEmision: true,
      };
    },
    [clearTelefono.toString()]: (
      state: MulticotizadorState,
      action: ReturnType<typeof clearTelefono>
    ): MulticotizadorState => {
      return {
        ...state,
        telefonoSelected: { value: null, isMobile: true },
        sendPostSolicitudEmision: true,
      };
    },
    [clearEmail.toString()]: (
      state: MulticotizadorState,
      action: ReturnType<typeof clearEmail>
    ): MulticotizadorState => {
      return {
        ...state,
        emailSelected: { value: null, selected: false },
        sendPostSolicitudEmision: true,
      };
    },
    [clearDominio.toString()]: (
      state: MulticotizadorState,
      action: ReturnType<typeof clearDominio>
    ): MulticotizadorState => {
      return {
        ...state,
        dominioSelected: { value: null, selected: false },
        sendPostSolicitudEmision: true,
      };
    },
    [clearMotor.toString()]: (
      state: MulticotizadorState,
      action: ReturnType<typeof clearMotor>
    ): MulticotizadorState => {
      return {
        ...state,
        motorSelected: { value: null, selected: false },
        sendPostSolicitudEmision: true,
      };
    },
    [clearNumeroChasis.toString()]: (
      state: MulticotizadorState,
      action: ReturnType<typeof clearNumeroChasis>
    ): MulticotizadorState => {
      return {
        ...state,
        numeroChasisSelected: { value: null, selected: false },
        sendPostSolicitudEmision: true,
      };
    },
    [clearValidadorDatosPago.toString()]: (
      state: MulticotizadorState,
      action: ReturnType<typeof clearValidadorDatosPago>
    ): MulticotizadorState => {
      return {
        ...state,
        validadorDatosPagoSelected: { value: null, selected: false },
        sendPostSolicitudEmision: true,
      };
    },
    [clearMarcaTarjetaCredito.toString()]: (
      state: MulticotizadorState,
      action: ReturnType<typeof clearMarcaTarjetaCredito>
    ): MulticotizadorState => {
      return {
        ...state,
        marcaTarjetaCreditoSelected: { value: null, selected: false },
        sendPostSolicitudEmision: true,
      };
    },
    [clearTarjetaDeMercantil.toString()]: (
      state: MulticotizadorState,
      action: ReturnType<typeof clearTarjetaDeMercantil>
    ): MulticotizadorState => {
      return {
        ...state,
        tarjetaDeMercantil: { value: false },
      };
    },
    [clearFechaVencimientoTarjeta.toString()]: (
      state: MulticotizadorState,
      action: ReturnType<typeof clearFechaVencimientoTarjeta>
    ): MulticotizadorState => {
      return {
        ...state,
        fechaVencimientoTarjetaSelected: { value: null, selected: false },
        sendPostSolicitudEmision: true,
      };
    },
    [clearOmitirCargaFotos.toString()]: (
      state: MulticotizadorState,
      action: ReturnType<typeof clearOmitirCargaFotos>
    ): MulticotizadorState => {
      return {
        ...state,
        fotoFrenteVehiculoSelected: {
          ...state.fotoFrenteVehiculoSelected,
          selected: false,
        },
        fotoTraseraVehiculoSelected: {
          ...state.fotoTraseraVehiculoSelected,
          selected: false,
        },
        fotoLateralIzquierdoVehiculoSelected: {
          ...state.fotoLateralIzquierdoVehiculoSelected,
          selected: false,
        },
        fotoLateralDerechoVehiculoSelected: {
          ...state.fotoLateralDerechoVehiculoSelected,
          selected: false,
        },
      };
    },
    [clearFotoFrenteVehiculo.toString()]: (
      state: MulticotizadorState,
      action: ReturnType<typeof clearFotoFrenteVehiculo>
    ): MulticotizadorState => {
      return {
        ...state,
        fotoFrenteVehiculoSelected: {
          ...state.fotoFrenteVehiculoSelected,
          selected: false,
        },
      };
    },
    [clearFotoTraseraVehiculo.toString()]: (
      state: MulticotizadorState,
      action: ReturnType<typeof clearFotoTraseraVehiculo>
    ): MulticotizadorState => {
      return {
        ...state,
        fotoTraseraVehiculoSelected: {
          ...state.fotoTraseraVehiculoSelected,
          selected: false,
        },
      };
    },
    [clearFotoLateralIzquierdoVehiculo.toString()]: (
      state: MulticotizadorState,
      action: ReturnType<typeof clearFotoLateralIzquierdoVehiculo>
    ): MulticotizadorState => {
      return {
        ...state,
        fotoLateralIzquierdoVehiculoSelected: {
          ...state.fotoLateralIzquierdoVehiculoSelected,
          selected: false,
        },
      };
    },
    [clearFotoLateralDerechoVehiculo.toString()]: (
      state: MulticotizadorState,
      action: ReturnType<typeof clearFotoLateralDerechoVehiculo>
    ): MulticotizadorState => {
      return {
        ...state,
        fotoLateralDerechoVehiculoSelected: {
          ...state.fotoLateralDerechoVehiculoSelected,
          selected: false,
        },
      };
    },
    [clearVigenciaDesde.toString()]: (
      state: MulticotizadorState,
      action: ReturnType<typeof clearVigenciaDesde>
    ): MulticotizadorState => {
      return {
        ...state,
        fechaVigenciaDesdeSelected: { value: null, selected: false },
        sendPostSolicitudEmision: true,
      };
    },

    [clearCurrentPoliza.toString()]: (
      state: MulticotizadorState,
      action: ReturnType<typeof clearCurrentPoliza>
    ): MulticotizadorState => {
      return {
        ...state,
        currentPoliza: {
          value: state.currentPoliza.value,
          open: false,
          contratada: false,
        },
      };
    },
    [clearContratarCurrentPoliza.toString()]: (
      state: MulticotizadorState,
      action: ReturnType<typeof clearContratarCurrentPoliza>
    ): MulticotizadorState => {
      return {
        ...state,
        currentPoliza: {
          value: state.currentPoliza.value,
          open: true,
          contratada: false,
        },
      };
    },

    [setAgencia.toString()]: (
      state: MulticotizadorState,
      action: ReturnType<typeof setAgencia>
    ): MulticotizadorState => {
      return {
        ...state,
        agenciaSelected: action.payload.value as AgenciaSeleccionada,
      };
    },
    [setVehiculoMarca.toString()]: (
      state: MulticotizadorState,
      action: ReturnType<typeof setVehiculoMarca>
    ): MulticotizadorState => {
      return {
        ...state,
        marcaSelected: action.payload.value,
      };
    },
    [setVehiculoAnio.toString()]: (
      state: MulticotizadorState,
      action: ReturnType<typeof setVehiculoAnio>
    ): MulticotizadorState => {
      return {
        ...state,
        anioSelected: action.payload.value,
      };
    },
    [setVehiculoModelo.toString()]: (
      state: MulticotizadorState,
      action: ReturnType<typeof setVehiculoModelo>
    ): MulticotizadorState => {
      return {
        ...state,
        modeloSelected: action.payload.value,
      };
    },
    [setProvincia.toString()]: (
      state: MulticotizadorState,
      action: ReturnType<typeof setProvincia>
    ): MulticotizadorState => {
      return {
        ...state,
        provinciaSelected: action.payload.value,
      };
    },
    [setLocalidad.toString()]: (
      state: MulticotizadorState,
      action: ReturnType<typeof setLocalidad>
    ): MulticotizadorState => {
      return {
        ...state,
        localidadSelected: action.payload.value,
      };
    },
    [setCodigoPostal.toString()]: (
      state: MulticotizadorState,
      action: ReturnType<typeof setCodigoPostal>
    ): MulticotizadorState => {
      return {
        ...state,
        codigoPostalSelected: action.payload.value,
      };
    },
    [setVehiculoUso.toString()]: (
      state: MulticotizadorState,
      action: ReturnType<typeof setVehiculoUso>
    ): MulticotizadorState => {
      return {
        ...state,
        usoSelected: action.payload.value,
      };
    },
    [setCondicionFiscal.toString()]: (
      state: MulticotizadorState,
      action: ReturnType<typeof setCondicionFiscal>
    ): MulticotizadorState => {
      return {
        ...state,
        condicionFiscalSelected: action.payload.value,
      };
    },
    [setTieneGNC.toString()]: (
      state: MulticotizadorState,
      action: ReturnType<typeof setTieneGNC>
    ): MulticotizadorState => {
      return {
        ...state,
        tieneGNC: action.payload.value,
      };
    },
    [setFormaDePago.toString()]: (
      state: MulticotizadorState,
      action: ReturnType<typeof setFormaDePago>
    ): MulticotizadorState => {
      return {
        ...state,
        formaDePagoSelected: action.payload.value,
      };
    },
    [setDni.toString()]: (
      state: MulticotizadorState,
      action: ReturnType<typeof setDni>
    ): MulticotizadorState => {
      return {
        ...state,
        dniSelected: action.payload.value,
      };
    },
    [setDniDuplicado.toString()]: (
      state: MulticotizadorState,
      action: ReturnType<typeof setDniDuplicado>
    ): MulticotizadorState => {
      return {
        ...state,
        dniDuplicado: action.payload.value,
      };
    },
    [setSexo.toString()]: (
      state: MulticotizadorState,
      action: ReturnType<typeof setSexo>
    ): MulticotizadorState => {
      return {
        ...state,
        sexoSelected: action.payload.value,
      };
    },
    [setEsPersonaValida.toString()]: (
      state: MulticotizadorState,
      action: ReturnType<typeof setEsPersonaValida>
    ): MulticotizadorState => {
      return {
        ...state,
        esPersonaValida: action.payload.value,
      };
    },
    [setPersona.toString()]: (
      state: MulticotizadorState,
      action: ReturnType<typeof setPersona>
    ): MulticotizadorState => {
      return {
        ...state,
        nombreSelected: action.payload.value.nombres,
        apellidoSelected: action.payload.value.apellido,
        cuilSelected: { value: action.payload.value.cuil, selected: true },
        fechaNacimientoSelected: {
          value: action.payload.value.fechaNacimiento,
          selected: true,
        },
        esPersonaValida: { value: true, selected: true },
      };
    },
    [setFechaNacimiento.toString()]: (
      state: MulticotizadorState,
      action: ReturnType<typeof setFechaNacimiento>
    ): MulticotizadorState => {
      return {
        ...state,
        fechaNacimientoSelected: action.payload.value,
      };
    },
    [setNombre.toString()]: (
      state: MulticotizadorState,
      action: ReturnType<typeof setNombre>
    ): MulticotizadorState => {
      return {
        ...state,
        nombreSelected: action.payload.value,
      };
    },
    [setApellido.toString()]: (
      state: MulticotizadorState,
      action: ReturnType<typeof setApellido>
    ): MulticotizadorState => {
      return {
        ...state,
        apellidoSelected: action.payload.value,
      };
    },
    [setCuil.toString()]: (
      state: MulticotizadorState,
      action: ReturnType<typeof setCuil>
    ): MulticotizadorState => {
      return {
        ...state,
        cuilSelected: action.payload.value,
      };
    },
    [setDireccion.toString()]: (
      state: MulticotizadorState,
      action: ReturnType<typeof setDireccion>
    ): MulticotizadorState => {
      return {
        ...state,
        direccionSelected: action.payload.value,
      };
    },
    [setTelefono.toString()]: (
      state: MulticotizadorState,
      action: ReturnType<typeof setTelefono>
    ): MulticotizadorState => {
      return {
        ...state,
        telefonoSelected: action.payload.value,
      };
    },
    [setEmail.toString()]: (
      state: MulticotizadorState,
      action: ReturnType<typeof setEmail>
    ): MulticotizadorState => {
      return {
        ...state,
        emailSelected: action.payload.value,
      };
    },
    [setDominio.toString()]: (
      state: MulticotizadorState,
      action: ReturnType<typeof setDominio>
    ): MulticotizadorState => {
      return {
        ...state,
        dominioSelected: action.payload.value,
      };
    },
    [setMotor.toString()]: (
      state: MulticotizadorState,
      action: ReturnType<typeof setMotor>
    ): MulticotizadorState => {
      return {
        ...state,
        motorSelected: action.payload.value,
      };
    },
    [setNumeroChasis.toString()]: (
      state: MulticotizadorState,
      action: ReturnType<typeof setNumeroChasis>
    ): MulticotizadorState => {
      return {
        ...state,
        numeroChasisSelected: action.payload.value,
      };
    },
    [setValidadorDatosPago.toString()]: (
      state: MulticotizadorState,
      action: ReturnType<typeof setValidadorDatosPago>
    ): MulticotizadorState => {
      return {
        ...state,
        validadorDatosPagoSelected: action.payload.value,
      };
    },
    [setMarcaTarjetaCredito.toString()]: (
      state: MulticotizadorState,
      action: ReturnType<typeof setMarcaTarjetaCredito>
    ): MulticotizadorState => {
      return {
        ...state,
        marcaTarjetaCreditoSelected: action.payload.value,
      };
    },
    [setTarjetaDeMercantil.toString()]: (
      state: MulticotizadorState,
      action: ReturnType<typeof setTarjetaDeMercantil>
    ): MulticotizadorState => {
      return {
        ...state,
        tarjetaDeMercantil: action.payload.value,
      };
    },
    [setFechaVencimientoTarjeta.toString()]: (
      state: MulticotizadorState,
      action: ReturnType<typeof setFechaVencimientoTarjeta>
    ): MulticotizadorState => {
      return {
        ...state,
        fechaVencimientoTarjetaSelected: action.payload.value,
      };
    },

    [setOmitirCargaFotos.toString()]: (
      state: MulticotizadorState,
      action: ReturnType<typeof setOmitirCargaFotos>
    ): MulticotizadorState => {
      return {
        ...state,
        fotoFrenteVehiculoSelected: action.payload.value,
        fotoTraseraVehiculoSelected: action.payload.value,
        fotoLateralIzquierdoVehiculoSelected: action.payload.value,
        fotoLateralDerechoVehiculoSelected: action.payload.value,
      };
    },
    [setFotoFrenteVehiculo.toString()]: (
      state: MulticotizadorState,
      action: ReturnType<typeof setFotoFrenteVehiculo>
    ): MulticotizadorState => {
      return {
        ...state,
        fotoFrenteVehiculoSelected: action.payload.value,
      };
    },
    [setFotoTraseraVehiculo.toString()]: (
      state: MulticotizadorState,
      action: ReturnType<typeof setFotoTraseraVehiculo>
    ): MulticotizadorState => {
      return {
        ...state,
        fotoTraseraVehiculoSelected: action.payload.value,
      };
    },
    [setFotoLateralIzquierdoVehiculo.toString()]: (
      state: MulticotizadorState,
      action: ReturnType<typeof setFotoLateralIzquierdoVehiculo>
    ): MulticotizadorState => {
      return {
        ...state,
        fotoLateralIzquierdoVehiculoSelected: action.payload.value,
      };
    },
    [setFotoLateralDerechoVehiculo.toString()]: (
      state: MulticotizadorState,
      action: ReturnType<typeof setFotoLateralDerechoVehiculo>
    ): MulticotizadorState => {
      return {
        ...state,
        fotoLateralDerechoVehiculoSelected: action.payload.value,
      };
    },
    [setInitialCurrentPoliza.toString()]: (
      state: MulticotizadorState,
      action: ReturnType<typeof setInitialCurrentPoliza>
    ): MulticotizadorState => {
      return {
        ...state,
        currentPoliza: action.payload.value,
      };
    },
    [setCurrentPoliza.toString()]: (
      state: MulticotizadorState,
      action: ReturnType<typeof setCurrentPoliza>
    ): MulticotizadorState => {
      return {
        ...state,
        currentPoliza: action.payload.value,
      };
    },
    [setContratarCurrentPoliza.toString()]: (
      state: MulticotizadorState,
      action: ReturnType<typeof setContratarCurrentPoliza>
    ): MulticotizadorState => {
      return {
        ...state,
        currentPoliza: action.payload.value,
      };
    },
    [setVigenciaDesde.toString()]: (
      state: MulticotizadorState,
      action: ReturnType<typeof setVigenciaDesde>
    ): MulticotizadorState => {
      return {
        ...state,
        fechaVigenciaDesdeSelected: action.payload.value,
      };
    },

    //CONTINUAR MULTICOTIZACIONES
    [postRecotizarInited.toString()]: (
      state: MulticotizadorState,
      action: ReturnType<typeof postRecotizarInited>
    ): MulticotizadorState => {
      return {
        ...state,
        sendPostCotizacion: false,
      };
    },
    [postRecotizarCompleted.toString()]: (
      state: MulticotizadorState,
      action: ReturnType<typeof postRecotizarCompleted>
    ): MulticotizadorState => {
      return {
        ...state,
        multicotizacionInitialStage: stages.recotizar,
        sendPostCotizacion: false,
      };
    },
    [fetchRecotizarFailed.toString()]: (
      state: MulticotizadorState,
      action: ReturnType<typeof fetchRecotizarFailed>
    ): MulticotizadorState => {
      return {
        ...state,
        multicotizacionInitialStage: stages.initialStage,
        sendPostCotizacion: false,
      };
    },
    //CONTINUAR MULTICOTIZACIONES
    [fetchContinuarSolicitudInited.toString()]: (
      state: MulticotizadorState,
      action: ReturnType<typeof fetchContinuarSolicitudInited>
    ): MulticotizadorState => {
      return {
        ...state,
        cotizacionARecotizar: null,
      };
    },
    [fetchContinuarSolicitudCompleted.toString()]: (
      state: MulticotizadorState,
      action: ReturnType<typeof fetchContinuarSolicitudCompleted>
    ): MulticotizadorState => {
      const data = action.payload.response;
      return {
        ...state,
        multicotizacionInitialStage: stages.continuarSolicitud,
        agenciaSelected: {
          value: data.agenciaId,
          // El detalle no trae `agencia`: el label queda undefined.
          label: data.agencia as string | null,
          selected: true,
        },
        marcaSelected: { value: data.marca, label: data.marca },
        // `codia` y `anio` llegan como número y se guardan así, sin convertir:
        // es lo que después viaja en los pedidos. Ver MulticotizadorState.
        modeloSelected: {
          value: data.codia as unknown as string,
          label: data.modelo,
        },
        anioSelected: {
          value: data.anio as unknown as string,
          label: data.anio as unknown as string,
        },
        codigoPostalSelected: data.codigoPostal,
        usoSelected: { value: data.uso, label: data.uso },
        tieneGNC: { value: data.tieneGNC, selected: true },
        formaDePagoSelected: { value: data.formaPago, label: data.formaPago },
        cotizacionARecotizar: data.cotizacionARecotizar,
        cotizacionId: data.cotizacionId,

        currentPoliza: {
          value: data.currentPoliza,
          open: true,
          contratada: true,
        },
      };
    },
    [fetchContinuarSolicitudFailed.toString()]: (
      state: MulticotizadorState,
      action: ReturnType<typeof fetchContinuarSolicitudFailed>
    ): MulticotizadorState => {
      return {
        ...state,
        multicotizacionInitialStage: stages.initialStage,
      };
    },
    //GET MULTICOTIZACIONES
    [getDetalleCotizacionInited.toString()]: (
      state: MulticotizadorState,
      action: ReturnType<typeof getDetalleCotizacionInited>
    ): MulticotizadorState => {
      return {
        ...state,
        isLoading: true,
      };
    },
    [getDetalleCotizacionCompleted.toString()]: (
      state: MulticotizadorState,
      action: ReturnType<typeof getDetalleCotizacionCompleted>
    ): MulticotizadorState => {
      return {
        ...state,
        isLoading: false,
        detalleCotizacion: action.payload.detalleCotizacion,
        polizasRequestCount: state.polizasRequestCount + 1,
      };
    },
    [getDetalleCotizacionFailed.toString()]: (
      state: MulticotizadorState,
      action: ReturnType<typeof getDetalleCotizacionFailed>
    ): MulticotizadorState => {
      return {
        ...state,
        isLoading: false,
      };
    },
    //POST MULTICOTIZACIONES
    [postPedidoCotizacionInited.toString()]: (
      state: MulticotizadorState,
      action: ReturnType<typeof postPedidoCotizacionInited>
    ): MulticotizadorState => {
      return {
        ...state,
        cotizacionId: null,
        detalleCotizacion: null,
        polizasRequestCount: 0,
        isLoading: true,
      };
    },
    [postPedidoCotizacionCompleted.toString()]: (
      state: MulticotizadorState,
      action: ReturnType<typeof postPedidoCotizacionCompleted>
    ): MulticotizadorState => {
      return {
        ...state,
        isLoading: false,
        cotizacionId: action.payload.response,
        sendPostCotizacion: false,
      };
    },
    [postPedidoCotizacionFailed.toString()]: (
      state: MulticotizadorState,
      action: ReturnType<typeof postPedidoCotizacionFailed>
    ): MulticotizadorState => {
      return {
        ...state,
        isLoading: false,
        sendPostCotizacion: false,
      };
    },

    //POST DATOS CONTACTO
    [postDatosContactoInited.toString()]: (
      state: MulticotizadorState,
      action: ReturnType<typeof postDatosContactoInited>
    ): MulticotizadorState => {
      return {
        ...state,
        isLoading: true,
        postDatosContactoError: null,
      };
    },
    [postDatosContactoCompleted.toString()]: (
      state: MulticotizadorState,
      action: ReturnType<typeof postDatosContactoCompleted>
    ): MulticotizadorState => {
      return {
        ...state,
        isLoading: false,
        sendPostSolicitudEmision: false,
      };
    },
    [postDatosContactoFailed.toString()]: (
      state: MulticotizadorState,
      action: ReturnType<typeof postDatosContactoFailed>
    ): MulticotizadorState => {
      return {
        ...state,
        isLoading: false,
        sendPostSolicitudEmision: false,
        postDatosContactoError: true,
      };
    },
    //GET IDENTIDAD
    [fetchIdentidadInited.toString()]: (
      state: MulticotizadorState,
      action: ReturnType<typeof fetchIdentidadInited>
    ): MulticotizadorState => {
      return {
        ...state,
        isLoading: true,
        nombreSelected: null,
        apellidoSelected: null,
        fechaNacimientoSelected: { value: null, selected: false },
        cuilSelected: { value: null, selected: false },
        dniDuplicado: null,
      };
    },
    [fetchIdentidadCompleted.toString()]: (
      state: MulticotizadorState,
      action: ReturnType<typeof fetchIdentidadCompleted>
    ): MulticotizadorState => {
      return {
        ...state,
        isLoading: false,
        nombreSelected: action.payload.response[0].nombres,
        apellidoSelected: action.payload.response[0].apellido,
        fechaNacimientoSelected: {
          value: action.payload.response[0].fechaNacimiento,
          selected: true,
        },
        cuilSelected: {
          value: action.payload.response[0].cuil,
          selected: true,
        },
        dniDuplicado:
          action.payload.response.length > 1 ? action.payload.response : null,
      };
    },
    [fetchIdentidadFailed.toString()]: (
      state: MulticotizadorState,
      action: ReturnType<typeof fetchIdentidadFailed>
    ): MulticotizadorState => {
      return {
        ...state,
        isLoading: false,
        nombreSelected: null,
        apellidoSelected: null,
        fechaNacimientoSelected: {
          value: null,
          selected: false,
        },
        dniDuplicado: [],
      };
    },
  },
  initialState
);

export default multicotizadorReducer;
export {
  clear,
  clearAgencia,
  setAgencia,
  clearVehiculoMarca,
  setVehiculoMarca,
  clearVehiculoAnio,
  setVehiculoAnio,
  clearVehiculoModelo,
  setVehiculoModelo,
  clearProvincia,
  setProvincia,
  clearLocalidad,
  setLocalidad,
  clearCodigoPostal,
  setCodigoPostal,
  clearVehiculoUso,
  setVehiculoUso,
  clearCondicionFiscal,
  setCondicionFiscal,
  clearTieneGNC,
  setTieneGNC,
  clearFormaDePago,
  setFormaDePago,
  clearDni,
  setDni,
  clearSexo,
  setSexo,
  setPersona,
  clearEsPersonaValida,
  setEsPersonaValida,
  clearDniDuplicado,
  setDniDuplicado,
  clearFechaNacimiento,
  setFechaNacimiento,
  clearNombre,
  setNombre,
  clearApellido,
  setApellido,
  clearCuil,
  setCuil,
  clearDireccion,
  setDireccion,
  clearTelefono,
  setTelefono,
  clearEmail,
  setEmail,
  clearDominio,
  setDominio,
  clearMotor,
  setMotor,
  clearNumeroChasis,
  setNumeroChasis,
  clearValidadorDatosPago,
  setValidadorDatosPago,
  clearMarcaTarjetaCredito,
  setMarcaTarjetaCredito,
  clearTarjetaDeMercantil,
  setTarjetaDeMercantil,
  clearFechaVencimientoTarjeta,
  setFechaVencimientoTarjeta,
  setInitialCurrentPoliza,
  clearCurrentPoliza,
  setCurrentPoliza,
  clearContratarCurrentPoliza,
  setContratarCurrentPoliza,
  clearFotoFrenteVehiculo,
  setFotoFrenteVehiculo,
  clearFotoTraseraVehiculo,
  setFotoTraseraVehiculo,
  clearFotoLateralIzquierdoVehiculo,
  setFotoLateralIzquierdoVehiculo,
  clearFotoLateralDerechoVehiculo,
  setFotoLateralDerechoVehiculo,
  clearVigenciaDesde,
  setVigenciaDesde,
  clearOmitirCargaFotos,
  setOmitirCargaFotos,
};

export const recotizar = (id: string) => {
  return async (dispatch: Dispatch, getState: GetState, services: Services) => {
    dispatch(postRecotizarInited());
    try {
      const response = await services.api.multicotizador().postRecotizacion(id);
      dispatch(postRecotizarCompleted());
      dispatch(postPedidoCotizacionCompleted(response.cotizacionId));
      dispatch(getDetalleCotizacion(response.cotizacionId));
    } catch (error) {
      console.error(error);
      dispatch(fetchRecotizarFailed(error));
    }
  };
};

export const continuarSolicitud = (
  id: string,
  currentPoliza: PolizaCotizada
) => {
  return async (dispatch: Dispatch, getState: GetState, services: Services) => {
    dispatch(fetchContinuarSolicitudInited());
    try {
      const response = await services.api
        .cotizaciones()
        .getDetalleCotizacion(id);

      dispatch(
        fetchContinuarSolicitudCompleted({
          ...response,
          cotizacionARecotizar: id,
          currentPoliza,
        })
      );
    } catch (error) {
      console.error(error);
      dispatch(fetchContinuarSolicitudFailed(error));
    }
  };
};

export const fetchIdentidad = (documento: string, sexo: string) => {
  return async (dispatch: Dispatch, getState: GetState, services: Services) => {
    try {
      dispatch(fetchIdentidadInited());
      const response = await services.api
        .identidad()
        .getIdentidad(documento, sexo);
      // Los datos de la persona pueden venir nulos: Capitalize y Date los
      // reciben igual que antes de migrar.
      const personasMapeadas: PersonaIdentidad[] = response.personas.map(
        (objeto) => {
          return {
            nombres: Capitalize(objeto.nombres as string),
            apellido: Capitalize(objeto.apellido as string),
            fechaNacimiento: new Date(
              objeto.fechaNacimiento as string
            ).toUTCString(),
            cuil: objeto.cuil as string,
          };
        }
      );

      dispatch(fetchIdentidadCompleted(personasMapeadas));
    } catch (error) {
      console.log(error);
      dispatch(fetchIdentidadFailed(error));
    }
  };
};

export const postPedidoCotizacion = ({
  agenciaSelected,
  anioSelected,
  modeloSelected,
  localidadSelected,
  provinciaSelected,
  codigoPostalSelected,
  usoSelected,
  condicionFiscalSelected,
  tieneGNC,
  formaDePagoSelected,
  cotizacionARecotizar,
}: MulticotizadorState) => {
  return async (dispatch: Dispatch, getState: GetState, services: Services) => {
    try {
      dispatch(postPedidoCotizacionInited());

      const data: PostMulticotizacionRequest = {
        AgenciaId: agenciaSelected.value,
        ModeloVehiculo: modeloSelected.value,
        AnioVehiculo: anioSelected.value,
        CodigoPostal: codigoPostalSelected,
        Localidad: localidadSelected,
        ProvinciaId: provinciaSelected,
        Negocio: "MercadoAbierto",
        UsoDelVehiculo: esUsoComercial(usoSelected!.value)
          ? COMERCIAL
          : PARTICULAR,
        CondicionFiscal: condicionFiscalSelected
          ? condicionFiscalSelected.value
          : null,
        TieneGNC: tieneGNC.value,
        Periodo: "Anual",
        FormaPago: formaDePagoSelected.value,
        CotizacionARecotizar: cotizacionARecotizar,
      };

      const response = await services.api.multicotizador().postCotizacion(data);

      let cotizacionId: string | null = null;

      // Chequeo heredado del JS: la respuesta es un objeto, `.length` da
      // undefined, así que esa parte siempre pasa. Se deja como estaba.
      if (
        typeof response !== "undefined" &&
        response !== null &&
        (response as unknown as unknown[]).length !== 0
      ) {
        cotizacionId = response.cotizacionId;
      } else {
        throw new Error("fail on postCotizacion");
      }

      dispatch(postPedidoCotizacionCompleted(cotizacionId));

      dispatch(getDetalleCotizacion(cotizacionId));
    } catch (error) {
      console.error(error);
      dispatch(postPedidoCotizacionFailed(error));
    }
  };
};

export const getDetalleCotizacion = (id: string) => {
  return async (dispatch: Dispatch, getState: GetState, services: Services) => {
    try {
      dispatch(getDetalleCotizacionInited());

      const response = await services.api
        .cotizaciones()
        .getDetalleCotizacion(id);

      dispatch(getDetalleCotizacionCompleted(response));
    } catch (error) {
      console.error(error);
      dispatch(getDetalleCotizacionFailed(error));
    }
  };
};

export const postDatosContacto = ({
  cotizacionId,
  nombreSelected,
  apellidoSelected,
  dniSelected,
  telefonoSelected,
  currentPoliza,
  direccionSelected,
  fechaNacimientoSelected,
  cuilSelected,
  sexoSelected,
  emailSelected,
  dominioSelected,
  motorSelected,
  numeroChasisSelected,
  validadorDatosPagoSelected,
  fotoFrenteVehiculoSelected,
  fotoTraseraVehiculoSelected,
  fotoLateralIzquierdoVehiculoSelected,
  fotoLateralDerechoVehiculoSelected,
  fechaVigenciaDesdeSelected,
  marcaTarjetaCreditoSelected,
  fechaVencimientoTarjetaSelected,
}: MulticotizadorState) => {
  return async (dispatch: Dispatch, getState: GetState, services: Services) => {
    try {
      dispatch(postDatosContactoInited());

      const fotosUploaded =
        fotoFrenteVehiculoSelected.value &&
        fotoTraseraVehiculoSelected.value &&
        fotoLateralIzquierdoVehiculoSelected.value &&
        fotoLateralDerechoVehiculoSelected.value;

      const fotos: string[] = [];

      if (fotosUploaded) {
        const fotoFrente = (
          await fileService("temp").download(fotoFrenteVehiculoSelected.value)
        ).url;

        const fotoTrasera = (
          await fileService("temp").download(fotoTraseraVehiculoSelected.value)
        ).url;

        const fotoLateralIzquierdo = (
          await fileService("temp").download(
            fotoLateralIzquierdoVehiculoSelected.value
          )
        ).url;

        const fotoLateralDerecho = (
          await fileService("temp").download(
            fotoLateralDerechoVehiculoSelected.value
          )
        ).url;

        fotos.push(fotoFrente);
        fotos.push(fotoTrasera);
        fotos.push(fotoLateralIzquierdo);
        fotos.push(fotoLateralDerecho);
      }

      const data: PostSolicitudEmisionRequest = {
        MulticotizacionId: cotizacionId,
        CoberturaSeleccionadaId: currentPoliza.value!.id,
        Nombre: nombreSelected,
        Apellido: apellidoSelected,
        DNI: dniSelected,
        Email: emailSelected.value,
        Telefono: telefonoSelected.value,
        EsTelefonoMovil: telefonoSelected.isMobile,
        Direccion: direccionSelected,
        FechaNacimiento: fechaNacimientoSelected.value,
        CUIL: cuilSelected.value,
        Sexo: sexoSelected.value,
        Dominio: dominioSelected.value,
        NumeroMotor: motorSelected.value,
        NumeroChasis: numeroChasisSelected.value,
        DatosPago: validadorDatosPagoSelected.value,
        UrlFotosVehiculo: fotos,
        VigenciaDesde: fechaVigenciaDesdeSelected.value,
        MarcaTarjetaCredito: marcaTarjetaCreditoSelected.value,
        FechaVencimientoTarjetaCredito: fechaVencimientoTarjetaSelected.value,
      };

      const response = await services.api
        .multicotizador()
        .postSolicitudEmision(data);

      dispatch(postDatosContactoCompleted(response));
    } catch (error) {
      console.error(
        "Error during postDatosContacto:",
        (error as Error).message || error
      );
      dispatch(postDatosContactoFailed(error));
    }
  };
};

//private functions
const upperCase = (str: string) => {
  return str.toUpperCase();
};
const Capitalize = (str: string) => {
  str = str.toLowerCase();
  var firstLetterRx = /(^|\s)[a-z]/g;
  return str.replace(firstLetterRx, upperCase);
};
